// Serviço para gerenciar conexões WhatsApp
import { request } from './api';

class ConnectionService {
  // Buscar todas as conexões
  async getConnections() {
    try {
      const response = await request('/api/whatsapp-connections');
      // O backend retorna diretamente o array, não dentro de um objeto 'data'
      return Array.isArray(response) ? response : (response.data || []);
    } catch (error) {
      console.error('Erro ao buscar conexões:', error);
      // Retornar array vazio em caso de erro
      return [];
    }
  }

  async getApiEndpoints() {
    try {
      const response = await request('/api/whatsapp-connections/endpoints');
      return response?.endpoints || [];
    } catch (error) {
      console.error('Erro ao buscar endpoints de API:', error);
      return [];
    }
  }

  // Criar nova conexão
  async createConnection(connectionData) {
    try {
      console.log('🔄 [ConnectionService] Criando conexão:', connectionData);
      
      // Mapear dados do frontend para o formato esperado pelo backend
      const backendData = {
        name: connectionData.name,
        isDefault: connectionData.isDefault || false,
        // Adicionar campos que estavam faltando
        type: connectionData.type || 'unofficial',
        messageCategory: connectionData.messageCategory || 'service',
        status: connectionData.status || 'OPENING',
        queueId: connectionData.queueId,
        greetingMessage: connectionData.greetingMessage,
        farewellMessage: connectionData.farewellMessage,
        outOfHoursMessage: connectionData.outOfHoursMessage,
        maxConnections: connectionData.maxConnections,
        sessionTimeout: connectionData.sessionTimeout,
        isActive: connectionData.isActive,
        autoReconnect: connectionData.autoReconnect,
        // Dados da sessão se estiver conectada
        // Aceitar tanto 'sessionId' quanto 'session' do formulário antigo
        sessionId: connectionData.sessionId || connectionData.session,
        qrCode: connectionData.qrCode
      };
      
      console.log('🔄 [ConnectionService] Dados para backend:', backendData);
      
      // Usar a função request do api.js - NÃO fazer JSON.stringify aqui
      // pois a função request já faz isso automaticamente
      const response = await request('/api/whatsapp-connections', {
        method: 'POST',
        body: backendData  // Remover JSON.stringify daqui
      });
      
      console.log('✅ [ConnectionService] Resposta do backend:', response);
      return response;
      
    } catch (error) {
      console.error('❌ [ConnectionService] Erro ao criar conexão:', error);
      throw error;
    }
  }

  // Atualizar conexão existente
  async updateConnection(id, connectionData) {
    try {
      const response = await request(`/api/whatsapp-connections/${id}`, {
        method: 'PUT',
        body: connectionData
      });
      return response?.data ?? response;
    } catch (error) {
      console.error('Erro ao atualizar conexão:', error);
      throw error;
    }
  }

  // Excluir conexão
  async deleteConnection(id) {
    try {
      await request(`/api/whatsapp-connections/${id}`, {
        method: 'DELETE'
      });
      return { success: true };
    } catch (error) {
      console.error('Erro ao excluir conexão:', error);
      throw error;
    }
  }

  // Iniciar sessão WhatsApp
  async startSession(id) {
    try {
      // Backend expõe POST /api/whatsapp-connections/:id/start
      const response = await request(`/api/whatsapp-connections/${id}/start`, {
        method: 'POST'
      });
      return response?.data ?? response;
    } catch (error) {
      console.error('Erro ao iniciar sessão:', error);
      throw error;
    }
  }

  // Parar sessão WhatsApp
  async stopSession(id) {
    try {
      // Backend expõe POST /api/whatsapp-connections/:id/stop
      const response = await request(`/api/whatsapp-connections/${id}/stop`, {
        method: 'POST'
      });
      return response?.data ?? response;
    } catch (error) {
      console.error('Erro ao parar sessão:', error);
      throw error;
    }
  }

  // Reiniciar sessão WhatsApp
  async restartSession(id) {
    try {
      // Primeiro parar
      await this.stopSession(id);
      // Depois iniciar novamente
      await new Promise(resolve => setTimeout(resolve, 2000)); // Aguardar 2 segundos
      const response = await this.startSession(id);
      return response;
    } catch (error) {
      console.error('Erro ao reiniciar sessão:', error);
      throw error;
    }
  }

  // Gerar QR Code
  async generateQRCode(sessionData) {
    try {
      const response = await request('/api/whatsapp-connections/generate-qr', {
        method: 'POST',
        body: sessionData
      });
      return response;
    } catch (error) {
      console.error('Erro ao gerar QR code:', error);
      throw error;
    }
  }

  // Obter QR Code da conexão por ID
  async getQRCode(id) {
    try {
      const response = await request(`/api/whatsapp-connections/${id}/qrcode`, {
        method: 'GET'
      });
      // Normalizar a resposta do backend para o formato esperado pelo frontend
      return {
        qrCode: response?.qrcode || '',
        status: response?.status || 'DISCONNECTED',
        retries: response?.retries ?? 0,
        lastDisconnect: response?.lastDisconnect || null
      };
    } catch (error) {
      console.error('Erro ao obter QR code:', error);
      throw error;
    }
  }

  // Verificar status da sessão
  async getSessionStatus(id) {
    try {
      const response = await request(`/api/whatsapp-connections/${id}/status/check`);
      return response;
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      throw error;
    }
  }

  // Desconectar sessão
  async disconnectSession(sessionId) {
    try {
      const response = await request(`/api/whatsapp-connections/${sessionId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Erro ao desconectar sessão:', error);
      throw error;
    }
  }

  // Obter estatísticas das conexões
  async getConnectionStats() {
    try {
      const connections = await this.getConnections();
      return {
        total: connections.length,
        active: connections.filter(c => c.status === 'CONNECTED').length,
        official: connections.filter(c => c.type === 'official').length,
        unofficial: connections.filter(c => c.type === 'unofficial').length
      };
    } catch (error) {
      console.error('Erro ao obter estatísticas:', error);
      return { total: 0, active: 0, official: 0, unofficial: 0 };
    }
  }

  // Definir conexão padrão
  async setDefaultConnection(id) {
    try {
      const response = await request(`/api/whatsapp-connections/${id}`, {
        method: 'PUT',
        body: { isDefault: true }
      });
      return response?.data ?? response;
    } catch (error) {
      console.error('Erro ao definir conexão padrão:', error);
      throw error;
    }
  }

  // Validar API oficial
  async validateOfficialApi(connection) {
    try {
      const response = await request('/api/whatsapp-connections/validate-official', {
        method: 'POST',
        body: connection
      });
      return response;
    } catch (error) {
      console.error('Erro ao validar API oficial:', error);
      throw error;
    }
  }

  // Testar conexão não oficial
  async testUnofficialConnection(sessionId) {
    try {
      const response = await request(`/api/whatsapp-connections/${sessionId}/test`, {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Erro ao testar conexão não oficial:', error);
      throw error;
    }
  }

  // Enviar mensagem de teste
  async sendTestMessage(connection, phoneNumber) {
    try {
      const response = await request('/api/whatsapp-connections/test-message', {
        method: 'POST',
        body: {
          connectionId: connection.id,
          phoneNumber,
          message: 'Mensagem de teste do WhatsApp'
        }
      });
      return response;
    } catch (error) {
      console.error('Erro ao enviar mensagem de teste:', error);
      throw error;
    }
  }

  // Desconectar todas as conexões ativas
  async disconnectAllSessions() {
    try {
      console.log('🔄 [ConnectionService] Desconectando todas as sessões...');
      
      const response = await request('/api/whatsapp-connections/disconnect-all', {
        method: 'POST'
      });
      
      console.log('✅ [ConnectionService] Resposta da desconexão em massa:', response);
      return response;
      
    } catch (error) {
      console.error('❌ [ConnectionService] Erro ao desconectar todas as sessões:', error);
      throw error;
    }
  }
}

// Criar instância única do serviço
const connectionService = new ConnectionService();

// Exportar tanto como default quanto como named export para compatibilidade
export { connectionService };
export default connectionService;
